import React, { useState, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Button, CircularProgress, FormControlLabel, Switch } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import { getServerUri } from "../../modules/uriUtility";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    marginLeft: "21.5rem",
    paddingBottom: "1rem",
    backgroundColor: "#404040"
  },
  content: {
    flexGrow: 1,
    backgroundColor: "#404040",
    color: "white",
    marginTop: "0.5%",
    marginRight: "1rem"
  },
  card: {
    margin: "0rem 0rem 0.5rem 0.5rem"
  },
  cardWrapper: {
    padding: "2rem"
  },
  cardTitleText: {
    fontSize: 26,
    fontWeight: 540,
    marginBottom: "1.5rem"
  },
  infoRow: {
    display: "flex",
    margin: "0.5rem 0",
    fontSize: "1rem"
  },
  infoLabel: {
    minWidth: "8rem",
    color: "#aaa"
  },
  infoValue: {
    color: "#fff",
    fontWeight: 500
  },
  actionRow: {
    display: "flex",
    alignItems: "center",
    marginTop: "1.5rem",
    gap: "1rem"
  },
  hiddenFileInput: {
    display: "none"
  },
  selectedFileName: {
    marginLeft: "1rem",
    fontSize: "0.9rem",
    color: "#bbb"
  },
  status: {
    marginTop: "1rem",
    fontSize: "0.9rem"
  },
  statusError: {
    color: "#ff7a7a"
  },
  statusOk: {
    color: "#7aff7a"
  },
  previewBoxError: {
    marginTop: "1rem",
    padding: "0.75rem 1rem",
    backgroundColor: "#3a2222",
    borderRadius: 4,
    fontSize: "0.85rem",
    color: "#ffb0b0",
    maxWidth: 640
  },
  description: {
    marginTop: "0.5rem",
    fontSize: "0.95rem",
    color: "#cfcfcf",
    maxWidth: 640
  }
}));

export default function VersionTransfer(props) {
  const classes = useStyles();
  const fileInputRef = useRef(null);

  const accessToken = localStorage.getItem("session.accessToken");

  const selectedGame = props.selectedGame;
  const selectedBase = props.selectedBase;
  const selectedVersion = props.selectedVersion;

  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [includeBinaries, setIncludeBinaries] = useState(false);
  const [status, setStatus] = useState({ kind: null, message: "" });

  const IMPORT_ERROR_MESSAGES = {
    BUNDLE_FILE_MISSING: "No file was attached to the request.",
    BUNDLE_INVALID_JSON:
      "The file is not valid JSON. Make sure you picked the right file (it must be a JSON exported from this Version Transfer tool, or a raw MongoDB version doc).",
    BUNDLE_NOT_AN_OBJECT: "The JSON does not look like a version document (expected an object at the root).",
    BUNDLE_MISSING_GAME_OR_BASE_ID: "The JSON is missing gameID or baseID. It does not look like a version document.",
    BUNDLE_MISSING_OR_EMPTY_TITLE: "The version has no title. Please fix the title field and try again.",
    BUNDLE_MISSING_OR_INVALID_GAMECONFIG: "The JSON has no gameConfig, or it is not in the expected shape.",
    BUNDLE_INVALID_IDS: "gameID or baseID is not a valid MongoDB ObjectId (must be a 24-char hex string).",
    BUNDLE_TOO_LARGE: "The file is bigger than the 25 MB limit. Try downloading without 'Include binaries'.",
    TARGET_GAME_NOT_FOUND:
      "The gameID in the JSON does not exist in this database. The target game must already exist here.",
    TARGET_BASE_NOT_FOUND:
      "The baseID in the JSON does not exist in this database. The target base must already exist here.",
    USER_IS_NOT_ALLOWED_ON_TARGET_GAME: "Your user does not have access to this game. Ask an admin to grant it.",
    USER_IS_NOT_ALLOWED_TO_TRANSFER_VERSIONS: "Your user does not have the 'versionTransfer' permission.",
    ACCESS_TOKEN_MISSING: "Your session has expired. Please log out and log in again.",
    ACCESS_TOKEN_NOT_RECOGNIZED: "Your session has expired. Please log out and log in again."
  };

  const friendlyImportError = (rawText) => {
    if (!rawText) return "Import failed for an unknown reason.";
    const trimmed = String(rawText).trim();
    if (IMPORT_ERROR_MESSAGES[trimmed]) return IMPORT_ERROR_MESSAGES[trimmed];
    if (trimmed.startsWith("IMPORT_FAILED:")) {
      const detail = trimmed.slice("IMPORT_FAILED:".length);
      if (detail.startsWith("ASSET_FILENAME_UNSAFE")) {
        return "An asset filename in the JSON looked unsafe (path traversal attempt). Import aborted.";
      }
      if (detail.startsWith("ASSET_INSERT_FAILED")) {
        return "The database refused to insert one of the assets. Nothing was committed.";
      }
      return "Import failed during processing: " + detail;
    }
    if (trimmed.startsWith("UPLOAD_ERROR:")) {
      return "Upload failed: " + trimmed.slice("UPLOAD_ERROR:".length);
    }
    return trimmed;
  };

  const optionLabel = (opt) => (opt && (opt.title || opt.name)) || "—";
  const gameTitle = optionLabel(selectedGame);
  const baseTitle = optionLabel(selectedBase);
  const versionTitle = optionLabel(selectedVersion);

  const handleDownload = async () => {
    if (!selectedVersion || !selectedVersion._id) {
      setStatus({ kind: "error", message: "No version is currently open. Open a version first." });
      return;
    }
    setIsExporting(true);
    setStatus({ kind: null, message: "" });
    try {
      const formBody = new URLSearchParams();
      formBody.append("accessToken", accessToken);
      formBody.append("versionID", String(selectedVersion._id));
      formBody.append("includeBinaries", includeBinaries ? "true" : "false");
      const response = await fetch(getServerUri("versionTransfer/export"), {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: formBody.toString()
      });
      if (!response.ok) {
        const text = await response.text();
        setStatus({ kind: "error", message: "Export failed: " + text });
        setIsExporting(false);
        return;
      }
      const disposition = response.headers.get("Content-Disposition") || "";
      let filename = "version.json";
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match) filename = match[1];

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setStatus({ kind: "ok", message: "Downloaded " + filename });
    } catch (err) {
      setStatus({ kind: "error", message: "Export error: " + err.message });
    }
    setIsExporting(false);
  };

  const sanitizeMongoExtendedJson = (text) =>
    String(text)
      .replace(/ObjectId\(\s*"([0-9a-fA-F]{24})"\s*\)/g, '"$1"')
      .replace(/ISODate\(\s*"([^"]+)"\s*\)/g, '"$1"')
      .replace(/NumberLong\(\s*"?(-?\d+)"?\s*\)/g, "$1")
      .replace(/NumberInt\(\s*"?(-?\d+)"?\s*\)/g, "$1")
      .replace(/NumberDecimal\(\s*"([^"]+)"\s*\)/g, "$1");

  const handleFilePick = async (e) => {
    const file = e.target.files && e.target.files[0];
    setFilePreview(null);
    setStatus({ kind: null, message: "" });
    if (!file) {
      setSelectedFile(null);
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setStatus({
        kind: "error",
        message: "File is " + (file.size / 1024 / 1024).toFixed(1) + " MB, larger than the 25 MB limit."
      });
      return;
    }

    setSelectedFile(file);

    try {
      const text = await file.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch (_) {
        parsed = JSON.parse(sanitizeMongoExtendedJson(text));
      }

      const issues = [];
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        issues.push("Root is not an object.");
      } else {
        if (!parsed.gameID) issues.push("Missing 'gameID'.");
        if (!parsed.baseID) issues.push("Missing 'baseID'.");
        if (!parsed.title || typeof parsed.title !== "string") issues.push("Missing or empty 'title'.");
        if (!parsed.gameConfig || typeof parsed.gameConfig !== "object" || Array.isArray(parsed.gameConfig)) {
          issues.push("Missing or invalid 'gameConfig'.");
        }
      }

      if (issues.length > 0) {
        setFilePreview({ valid: false, issues });
        setStatus({ kind: "error", message: "File is not a valid version JSON: " + issues.join(" ") });
        return;
      }

      setFilePreview({ valid: true });
    } catch (err) {
      setFilePreview({ valid: false, issues: ["File is not valid JSON."] });
      setStatus({ kind: "error", message: "Could not parse the file as JSON: " + err.message });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setStatus({ kind: "error", message: "Please select a JSON file first." });
      return;
    }
    if (filePreview && filePreview.valid === false) {
      setStatus({ kind: "error", message: "Cannot upload: the file failed validation. Pick a valid version JSON." });
      return;
    }
    setIsImporting(true);
    setStatus({ kind: null, message: "" });
    try {
      const formData = new FormData();
      formData.append("accessToken", accessToken);
      formData.append("file", selectedFile);
      const response = await fetch(getServerUri("versionTransfer/import"), {
        method: "POST",
        body: formData
      });
      const text = await response.text();
      if (!response.ok) {
        setStatus({ kind: "error", message: friendlyImportError(text) });
        setIsImporting(false);
        return;
      }
      let parsed = null;
      try {
        parsed = JSON.parse(text);
      } catch (e) {
        parsed = null;
      }
      const importedTitle = parsed && parsed.version ? parsed.version.title : "(imported)";
      const assetCount = parsed && parsed.assetsImported != null ? parsed.assetsImported : "?";
      setStatus({
        kind: "ok",
        message: "Imported '" + importedTitle + "' with " + assetCount + " assets. Reload to see it."
      });
      setSelectedFile(null);
      setFilePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      setStatus({ kind: "error", message: "Network error: " + err.message });
    }
    setIsImporting(false);
  };

  return (
    <div className={classes.root}>
      <main className={classes.content}>
        <Card className={classes.card}>
          <div className={classes.cardWrapper}>
            <Typography className={classes.cardTitleText} align="left">
              Download Version
            </Typography>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Game:</span>
              <span className={classes.infoValue}>{gameTitle}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Base:</span>
              <span className={classes.infoValue}>{baseTitle}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Version:</span>
              <span className={classes.infoValue}>{versionTitle}</span>
            </div>
            <Typography className={classes.description}>
              Downloads the currently open version as a JSON. Within the same server (test ↔ prod share storage) DB
              transfer is enough. Toggle "Include binaries" only if the target is a different server (e.g. taking from
              prod down to your local). To transfer a different version, open that version first and then come back
              here.
            </Typography>
            <FormControlLabel
              style={{ marginTop: "0.75rem", color: "white" }}
              control={
                <Switch
                  checked={includeBinaries}
                  onChange={(e) => setIncludeBinaries(e.target.checked)}
                  color="primary"
                />
              }
              label="Include binaries (cross-server transfer)"
            />
            <div className={classes.actionRow}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleDownload}
                disabled={isExporting || !selectedVersion}
              >
                {isExporting ? <CircularProgress size={20} /> : "Download"}
              </Button>
            </div>
          </div>
        </Card>

        <Card className={classes.card}>
          <div className={classes.cardWrapper}>
            <Typography className={classes.cardTitleText} align="left">
              Upload Version Bundle
            </Typography>
            <Typography className={classes.description}>
              Pick a version JSON exported from another environment or company. The bundle's gameID and baseID must
              already exist in this database and be assigned to your user. A new version will be created here with
              assets re-inserted under your company.
            </Typography>
            <div className={classes.actionRow}>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/json,.json"
                className={classes.hiddenFileInput}
                onChange={handleFilePick}
              />
              <Button
                variant="outlined"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={isImporting}
                style={{ color: "white", borderColor: "white" }}
              >
                Choose File
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpload}
                disabled={isImporting || !selectedFile || (filePreview && filePreview.valid === false)}
              >
                {isImporting ? <CircularProgress size={20} /> : "Upload"}
              </Button>
              {selectedFile && <span className={classes.selectedFileName}>{selectedFile.name}</span>}
            </div>
            {filePreview && filePreview.valid === false && (
              <div className={classes.previewBoxError}>
                <div>
                  <strong>This file cannot be imported:</strong>
                </div>
                <ul style={{ margin: "0.25rem 0 0 1rem", padding: 0 }}>
                  {filePreview.issues.map((issue, idx) => (
                    <li key={idx}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}
            {status.kind && (
              <div
                className={classes.status + " " + (status.kind === "error" ? classes.statusError : classes.statusOk)}
              >
                {status.message}
              </div>
            )}
          </div>
        </Card>
      </main>
    </div>
  );
}
